import React, { useEffect, lazy, Suspense } from 'react';
import MainLayout from '../layouts/MainLayout';
import Hero from '../components/Hero';
import { Helmet } from 'react-helmet';
import { generatePhotographyBusinessSchema, generateFAQSchema } from '../utils/schemaData';
import Portfolio from '../components/Portfolio';

// Lazy load components with prefetch
const Process = lazy(() => import('../components/Process'));
const AboutUs = lazy(() => import('../components/AboutUs'));
const FAQ = lazy(() => import('../components/FAQ'));
const Testimonials = lazy(() => import('../components/Testimonials'));
const CallToAction = lazy(() => import('../components/CallToAction'));
const ContactForm = lazy(() => import('../components/ContactForm'));
const InstagramFeed = lazy(() => import('../components/InstagramFeed'));

// Improved loading fallback
const LoadingFallback = () => (
  <div className="w-full flex justify-center items-center py-20">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-navy-800"></div>
  </div>
);

// Prepare FAQs for structured data
const faqs = [
  {
    question: "Jakie rodzaje fotografii oferujecie?",
    answer: "Specjalizujemy się w fotografii ślubnej, portretowej, rodzinnej oraz okolicznościowej. Wykonujemy zarówno sesje plenerowe, jak i studyjne."
  },
  {
    question: "Ile kosztuje sesja zdjęciowa?",
    answer: "Ceny sesji zdjęciowych zależą od wielu czynników, takich jak rodzaj sesji, lokalizacja, liczba zdjęć. Szczegółowy cennik dostępny jest po kontakcie."
  },
  {
    question: "Jak długo trzeba czekać na zdjęcia?",
    answer: "Czas oczekiwania na zdjęcia to zazwyczaj 2-3 tygodnie dla standardowych sesji oraz 4-6 tygodni dla reportaży ślubnych."
  },
  {
    question: "Czy dojeżdżacie na sesje poza miasto?",
    answer: "Tak, realizujemy sesje zdjęciowe na terenie całego kraju, a także za granicą. Koszty dojazdu ustalane są indywidualnie."
  }
];

// Prefetch components
const prefetchComponent = (importFn: () => Promise<any>) => {
  const prefetcher = setTimeout(() => {
    importFn().then(() => clearTimeout(prefetcher));
  }, 1000);
};

const Index = () => {
  // Scroll to top when page loads and prefetch components
  useEffect(() => {
    window.scrollTo(0, 0);

    // Prefetch important components
    prefetchComponent(() => import('../components/AboutUs'));
    prefetchComponent(() => import('../components/Process'));
    prefetchComponent(() => import('../components/InstagramFeed'));

    // Adding animation class to global styles
    const style = document.createElement('style');
    style.innerHTML = `
      .duration-1500 {
        transition-duration: 1500ms;
      }
    `;
    document.head.appendChild(style);

    // Hide reCAPTCHA badge to prevent errors
    const badges = document.getElementsByClassName('grecaptcha-badge');
    if (badges.length > 0) {
      for (let i = 0; i < badges.length; i++) {
        const badge = badges[i] as HTMLElement;
        if (badge) {
          badge.style.visibility = 'hidden';
        }
      }
    }

    return () => {
      document.head.removeChild(style);
      
      // Restore reCAPTCHA badges when component unmounts
      const badges = document.getElementsByClassName('grecaptcha-badge');
      if (badges.length > 0) {
        for (let i = 0; i < badges.length; i++) {
          const badge = badges[i] as HTMLElement;
          if (badge) {
            badge.style.visibility = 'visible';
          }
        }
      }
    };
  }, []);

  // Generate structured data
  const businessSchema = generatePhotographyBusinessSchema();
  const faqSchema = generateFAQSchema(faqs);

  return (
    <MainLayout>
      <Helmet>
        <title>Czułe Klisze | Naturalna fotografia i klasyczne filmy ślubne</title>
        <meta name="description" content="Czułe Klisze to profesjonalne studio fotograficzne specjalizujące się w naturalnej fotografii i klasycznych filmach ślubnych. Tworzymy ponadczasowe wspomnienia z wyjątkowych chwil." />
        <meta name="keywords" content="czułe klisze, fotografia ślubna, film ślubny, naturalna fotografia, studio fotograficzne, wesela, eventy, sesje zdjęciowe" />
        <meta property="og:title" content="Czułe Klisze | Naturalna fotografia i klasyczne filmy ślubne" />
        <meta property="og:description" content="Profesjonalne studio fotograficzne specjalizujące się w naturalnej fotografii i klasycznych filmach ślubnych. Tworzymy ponadczasowe wspomnienia z wyjątkowych chwil." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://czuleklisze.pl" />
        <meta property="og:image" content="/og-image.png" />
        <meta http-equiv="Content-Security-Policy" content="upgrade-insecure-requests" />
        <link rel="canonical" href="https://czuleklisze.pl" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(businessSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      
      <div className="w-full">
        <Hero />
        
        {/* Use Intersection Observer for better lazy loading */}
        <Suspense fallback={<LoadingFallback />}>
          <AboutUs />
        </Suspense>
        
        <Portfolio />
        
        <Suspense fallback={<LoadingFallback />}>
          <Process />
        </Suspense>
        
        <Suspense fallback={<LoadingFallback />}>
          <CallToAction />
        </Suspense>
        
        <div id="contact">
          <div className="pt-12 md:pt-16 bg-white">
            <div className="container-custom">
              <div className="text-center max-w-2xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-serif text-elegant-900 mb-6 tracking-tight">Formularz Kontaktowy</h2>
                <p className="text-elegant-600 mb-10 max-w-xl mx-auto font-inter font-light uppercase tracking-wide text-sm md:text-base">
                  Poznajmy się lepiej. W odpowiedzi na formularz kontaktowy otrzymasz ofertę
                  oraz dokładne szczegóły dotyczące naszej pracy.
                </p>
              </div>
            </div>
          </div>
          <Suspense fallback={<LoadingFallback />}>
            <ContactForm />
          </Suspense>
        </div>
        
        <Suspense fallback={<LoadingFallback />}>
          <Testimonials />
        </Suspense>
        
        <Suspense fallback={<LoadingFallback />}>
          <FAQ />
        </Suspense>
        
        <Suspense fallback={<LoadingFallback />}>
          <InstagramFeed />
        </Suspense>
      </div>
    </MainLayout>
  );
};

export default Index;
